import { useCallback, useEffect, useRef } from 'react'

// Remembers where the user was inside an article and restores them on
// the next visit. Stores a percentage (not pixel) so font-size and
// device width changes don't strand them at the wrong spot.
//
// Per-device via localStorage on purpose: the article view is mostly
// a single-session read, and a DB-backed cross-device sync isn't
// worth the extra table/RLS for the few times a user opens the same
// article on two devices.

const findScrollableAncestor = (el) => {
  let p = el?.parentElement
  while (p && p !== document.body) {
    const s = window.getComputedStyle(p)
    if (s.overflowY === 'auto' || s.overflowY === 'scroll') return p
    p = p.parentElement
  }
  return null
}

const keyFor = (articleId) => `phoenix:news-article-pos:${articleId}`

export function useArticleScrollPosition(articleId, readyToRestore) {
  const rootRef = useRef(null)
  const restoredRef = useRef(false)

  // Save on every scroll (rAF-throttled) and on unmount. The save
  // effect attaches once per article id, so navigating between
  // articles re-installs cleanly.
  useEffect(() => {
    if (!articleId) return undefined
    const scroller = findScrollableAncestor(rootRef.current)
    if (!scroller) return undefined
    let raf = 0
    const save = () => {
      const max = scroller.scrollHeight - scroller.clientHeight
      if (max <= 0) return
      const pct = Math.max(0, Math.min(100, (scroller.scrollTop / max) * 100))
      try { localStorage.setItem(keyFor(articleId), String(pct.toFixed(2))) } catch { /* ignore */ }
    }
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(save)
    }
    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(raf)
      save()
      scroller.removeEventListener('scroll', onScroll)
    }
  }, [articleId])

  // Restore once the caller marks the content "ready" (article body
  // + similar articles loaded enough that scrollHeight has settled).
  // Same retry-frames pattern as the news list — late paints can
  // grow the page after the first attempt.
  useEffect(() => {
    if (restoredRef.current) return
    if (!articleId) return
    if (!readyToRestore) return

    const raw = (() => { try { return localStorage.getItem(keyFor(articleId)) } catch { return null } })()
    const pct = raw ? parseFloat(raw) : 0
    // Don't waste cycles for top-of-article positions.
    if (!Number.isFinite(pct) || pct <= 1) {
      restoredRef.current = true
      return
    }
    const scroller = findScrollableAncestor(rootRef.current)
    if (!scroller) return

    let attempts = 0
    const maxAttempts = 45
    const settle = () => {
      if (attempts++ >= maxAttempts) { restoredRef.current = true; return }
      const max = scroller.scrollHeight - scroller.clientHeight
      const target = (pct / 100) * max
      scroller.scrollTop = target
      if (Math.abs(scroller.scrollTop - target) <= 4) {
        restoredRef.current = true
        return
      }
      requestAnimationFrame(settle)
    }
    requestAnimationFrame(settle)
  }, [articleId, readyToRestore])

  const clear = useCallback(() => {
    if (!articleId) return
    try { localStorage.removeItem(keyFor(articleId)) } catch { /* ignore */ }
  }, [articleId])

  return { rootRef, clear }
}
